import { create } from 'zustand';

import { clearPromptDraft } from '@/pages/dashboard/playground/prompt-draft';
import { clearAdsDrafts } from '@/pages/dashboard/ads/lib/draft';
import { analytics } from '@/shared/lib/analytics';
import { supabaseAuthService } from '@/shared/services/supabase-auth-service';
import type { User } from '@/shared/types/auth';

export type AuthState = {
  isAuthenticated: boolean;
  isInitializing: boolean;
  user: User | null;
  isCreditsLoading: boolean;
  login: (user: User) => void;
  logout: () => Promise<void>;
  resetAuthState: () => void;
  setInitializing: (value: boolean) => void;
  updateUser: (userData: Partial<User>) => void;
  updateCredits: (newCredits: number) => void;
  deductCredits: (amount: number) => void;
  addCredits: (amount: number) => void;
  setCredits: (credits: number) => void;
  /**
   * Update the full balance trio in one go. Use when the source (WS
   * creditUpdate, /auth/me poll) carries hold-aware fields. Optional
   * `heldCredits` / `availableCredits` are stored as-provided; pass
   * `undefined` to leave a field untouched.
   */
  setCreditBalance: (balance: {
    credits: number;
    heldCredits?: number;
    availableCredits?: number;
  }) => void;
  setCreditsLoading: (loading: boolean) => void;
  triggerCreditsLoading: () => void;
};

export const useAuthStore = create<AuthState>((set) => ({
  isAuthenticated: false,
  isInitializing: true,
  user: null,
  isCreditsLoading: false,

  login: (user) => {
    // Supabase handles token storage automatically
    set({ isAuthenticated: true, user, isCreditsLoading: false });

    // TODO: Set user context in Sentry for error tracking when available
  },

  logout: async () => {
    // Clear local state first
    set({ isAuthenticated: false, user: null, isCreditsLoading: false });

    // Privacy fix: the playground prompt draft (prompt-draft.ts) is
    // sessionStorage-backed, so a tab that stays open across a sign-out
    // doesn't clear it on its own — the next person who signs in on a
    // shared machine, in the SAME tab, would otherwise land on the
    // playground pre-filled with the previous user's unsent prompt text.
    // prompt-draft.ts stays the single owner of the storage key; this just
    // calls its exported clear function. Ads Studio's generator drafts
    // (ads/lib/draft.ts) are the same kind of text and are cleared the same way.
    clearPromptDraft();
    clearAdsDrafts();

    // Clear React Query cache manually
    if (typeof window !== 'undefined') {
      // Get query client from global if available
      const queryClient = (
        window as unknown as {
          __REACT_QUERY_CLIENT__?: {
            removeQueries: (options: { queryKey: string[] }) => void;
            clear: () => void;
          };
        }
      ).__REACT_QUERY_CLIENT__;
      if (queryClient) {
        queryClient.removeQueries({ queryKey: ['user', 'me'] });
        queryClient.clear(); // Clear all queries
      }
    }

    // Sign out from Supabase
    await supabaseAuthService.signOut();

    // Reset RudderStack user identity
    analytics.reset();

    // TODO: Clear user context in Sentry when available
  },

  resetAuthState: () => {
    // Reset auth state without calling Supabase signOut (to avoid triggering events)
    set({ isAuthenticated: false, user: null, isCreditsLoading: false });

    // TODO: Clear user context in Sentry when available
  },

  setInitializing: (value) => {
    set({ isInitializing: value });
  },

  updateUser: (userData) => {
    set((state) => ({
      user: state.user ? { ...state.user, ...userData } : null,
    }));
  },

  updateCredits: (newCredits) => {
    set((state) => ({
      user: state.user ? { ...state.user, credits: newCredits } : null,
    }));
  },

  deductCredits: (amount) => {
    set((state) => ({
      user: state.user
        ? { ...state.user, credits: Math.max(0, state.user.credits - amount) }
        : null,
    }));
  },

  addCredits: (amount) => {
    set((state) => ({
      user: state.user ? { ...state.user, credits: state.user.credits + amount } : null,
    }));
  },

  setCredits: (credits) => {
    set((state) => ({
      user: state.user ? { ...state.user, credits } : null,
    }));
  },

  setCreditBalance: ({ credits, heldCredits, availableCredits }) => {
    set((state) => {
      if (!state.user) return { user: null };
      const next = { ...state.user, credits };
      if (heldCredits !== undefined) {
        next.held_credits = heldCredits;
      }
      if (availableCredits !== undefined) {
        next.available_credits = availableCredits;
      }
      return { user: next };
    });
  },

  setCreditsLoading: (loading) => {
    set({ isCreditsLoading: loading });
  },

  triggerCreditsLoading: () => {
    set({ isCreditsLoading: true });
  },
}));
